import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { useLocation, useNavigate } from "react-router";

export const ORGANIZATIONAL_RECORD_ANCHOR = "organisationsakte";

type OrganizationalRecordSectionState = {
  /** Whether the record section is on screen for the current subject. */
  expanded: boolean;
  /** The section element, focused and scrolled into view whenever it opens. */
  sectionRef: RefObject<HTMLElement | null>;
  /** Opens the section for the current subject and puts the anchor into the URL, so a reload or a link lands on it. */
  open: () => void;
};

/**
 * The profile page and the company page reach the organisational record the same way: a button opens the
 * section under the page and moves `#organisationsakte` into the URL. Expansion is remembered per subject,
 * so moving from one subject to the next closes the record instead of showing the previous file for a render.
 */
export function useOrganizationalRecordSection(subjectId: string | undefined, canRead: boolean): OrganizationalRecordSectionState {
  const navigate = useNavigate();
  const location = useLocation();
  const sectionRef = useRef<HTMLElement>(null);
  const [expandedFor, setExpandedFor] = useState<string | null>(null);
  const expanded = canRead && Boolean(subjectId) && (expandedFor === subjectId || location.hash === `#${ORGANIZATIONAL_RECORD_ANCHOR}`);
  const scrollTo = useCallback(() => {
    const section = sectionRef.current;
    if (!section) return;
    section.focus({ preventScroll: true });
    section.scrollIntoView({ behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "start" });
  }, []);
  useEffect(() => {
    if (expanded) scrollTo();
  }, [expanded, subjectId, scrollTo]);
  const open = useCallback(() => {
    if (!subjectId) return;
    setExpandedFor(subjectId);
    navigate({ pathname: location.pathname, search: location.search, hash: `#${ORGANIZATIONAL_RECORD_ANCHOR}` }, { replace: true, preventScrollReset: true });
    if (expanded) scrollTo();
  }, [subjectId, navigate, location.pathname, location.search, expanded, scrollTo]);
  return { expanded, sectionRef, open };
}
